// useMissionRunActions.js

import { useCallback, useState } from 'react';

import {
  appendDerivedMissionTaskEvents,
  appendStatusTransitionTaskEvents,
  applyMissionTaskActivity,
  buildMissionRun,
  buildMissionRunsWithoutKey,
  buildMissionTaskEvent,
  buildMissionTaskNudgeDetail,
  buildNextMissionRunsPatch,
  buildReopenMissionTaskConfirmMessage,
  canNudgeMissionTask,
  canReopenMissionTask,
  canUnblockMissionTask,
  collectMissionTaskReopenCascadeTasks,
  normalizeMissionTaskEvents,
  reopenMissionRunTask,
  recomputeMissionRunTasks,
  NODE_MISSION_TASK_EVENT_TYPE,
} from 'node/missions/mission/utils/missionRunUtils';
import {
  getMissionTaskPersonKey,
  getMissionTaskPersonLabel,
} from 'missions/taskmodal/missionTaskModalUtils';
import {
  listChecklistsForNodeField,
  pickCanonicalChecklist,
} from 'services/checklists/ChecklistService';
import { getNodeType } from 'services/nodetypes/NodeTypesCacheService';
import {
  mintWorkflowStepTaskNodesForRun,
  WORKFLOW_STEP_TASK_TYPE_KEY,
} from 'node/missions/mission/utils/mintWorkflowStepTaskNodes';
import {
  computeWorkflowTaskChecklistProgress,
  isWorkflowTaskChecklistBlockingComplete,
  readWorkflowTaskChecklistRecipe,
  resolveTaskChecklistFieldName,
} from 'node/missions/mission/utils/workflowStepChecklistUtils';
import {
  buildHostLaunchAssigneesByTaskKey,
  collectWorkflowStepNodeKeys,
} from 'node/missions/mission/utils/workflowStepNodeUtils';
import { moveNodesToTrash } from 'services/nodes/NodeWriteService';
import { WORKFLOW_OUTCOME_COMPLETE } from 'workflows/workflowGraphUtils';

/**
 * useMissionRunActions
 *
 * Shared Mission run persistence handlers for the dashboard and wizard.
 *
 * @param {object} options - Hook inputs.
 * @param {object|null} options.node - Current anchor node.
 * @param {Function} options.onNodeChange - Persists node document patches.
 * @param {object|null} options.user - Current signed-in user.
 * @param {string|null} [options.baseKey] - Active base key for minted step TASKs.
 * @returns {object} Mission action handlers and local UI state.
 */
export function useMissionRunActions({
  node = null,
  onNodeChange,
  user = null,
  baseKey = null,
} = {}) {
  const [blockingReasonDrafts, setBlockingReasonDrafts] = useState({});
  const [blockingReasonErrors, setBlockingReasonErrors] = useState({});
  const [blockingTaskKey, setBlockingTaskKey] = useState('');
  const [nudgingTaskKey, setNudgingTaskKey] = useState('');
  const [savingMissionKey, setSavingMissionKey] = useState('');
  const [reopenConfirm, setReopenConfirm] = useState(null);

  const handleLaunchMission = useCallback(async (template, options = {}) => {
    if (!template?.key || !node?.key || typeof onNodeChange !== 'function') return;

    setSavingMissionKey(template.key);
    try {
      // People steps inherit the host record's people (e.g. Project assignees).
      // Launch preflight may override per task; otherwise every people step gets
      // the same host people set.
      const launchAssigneesByTaskKey = buildHostLaunchAssigneesByTaskKey(
        template,
        node,
        options?.launchAssigneesByTaskKey,
      );
      const draftRun = buildMissionRun(template, user, {
        ...options,
        launchAssigneesByTaskKey,
      });
      const tasksForMint = (Array.isArray(draftRun.tasks) ? draftRun.tasks : []).map((task) => {
        const taskKey = String(task?.key || '').trim();
        if (
          !taskKey
          || !Object.prototype.hasOwnProperty.call(launchAssigneesByTaskKey, taskKey)
        ) {
          return task;
        }

        return {
          ...task,
          assignees: launchAssigneesByTaskKey[taskKey],
        };
      });
      const mintedTasks = await mintWorkflowStepTaskNodesForRun({
        anchorNode: node,
        baseKey: baseKey || node?.baseKey || null,
        onProgress: options?.onProgress,
        tasks: tasksForMint,
        user,
      });
      const nextRun = {
        ...draftRun,
        tasks: mintedTasks,
        updatedAtMs: Date.now(),
      };
      const nextMissionRuns = buildNextMissionRunsPatch(node?.missionRuns, template.key, nextRun);

      if (typeof options?.onProgress === 'function') {
        try {
          options.onProgress({ phase: 'saving', completed: 0, total: 0 });
        } catch (error) {
          console.warn('[useMissionRunActions] onProgress failed:', error);
        }
      }

      await onNodeChange(node.key, {
        missionRuns: nextMissionRuns,
      });
      return template.key;
    } finally {
      setSavingMissionKey('');
    }
  }, [baseKey, node, onNodeChange, user]);

  const handleCancelMission = useCallback(async (template) => {
    if (!template?.key || !node?.key || typeof onNodeChange !== 'function') return false;

    const existingRun = node?.missionRuns?.[template.key] || null;
    const stepNodeKeys = collectWorkflowStepNodeKeys(existingRun);
    const nextMissionRuns = buildMissionRunsWithoutKey(node?.missionRuns, template.key);
    const trashedByKey = String(user?.key || user?.uid || '').trim() || null;

    setSavingMissionKey(template.key);
    try {
      // Soft-delete minted step TASK nodes so cancel does not leave orphans.
      if (stepNodeKeys.length > 0) {
        try {
          await moveNodesToTrash(stepNodeKeys, { trashedByKey });
        } catch (error) {
          console.error('Failed to trash workflow step tasks on cancel', {
            error: error?.message || String(error),
            stepNodeKeys,
            templateKey: template.key,
          });
        }
      }

      await onNodeChange(node.key, {
        missionRuns: nextMissionRuns,
      });
      return true;
    } finally {
      setSavingMissionKey('');
    }
  }, [node?.key, node?.missionRuns, onNodeChange, user]);

  const handleTaskStatusChange = useCallback(async (template, missionRun, taskKey, nextStatus, options = {}) => {
    if (!template?.key || !node?.key || !missionRun || typeof onNodeChange !== 'function') return;

    const normalizedTaskKey = String(taskKey || '').trim();
    const normalizedStatus = String(nextStatus || '').trim();
    const blockingReasonOverride = String(options?.blockingReason || '').trim();
    const failureDetailOverride = String(options?.failureDetail || '').trim();
    if (!normalizedTaskKey || !normalizedStatus) return;

    const targetTask = missionRun.tasks.find((task) => task.key === normalizedTaskKey) || null;
    if (!targetTask || targetTask.status === 'waiting' || normalizedStatus === 'waiting') return;
    if (targetTask.status === normalizedStatus) return;

    if (normalizedStatus === 'complete' && targetTask.taskChecklistRequired === true) {
      const recipe = readWorkflowTaskChecklistRecipe(targetTask);
      if (recipe.items.length > 0) {
        const stepNodeKey = String(targetTask?.nodeKey || '').trim();
        const baseKey = String(node?.baseKey || '').trim();
        if (stepNodeKey && baseKey) {
          try {
            const typeDef = getNodeType(WORKFLOW_STEP_TASK_TYPE_KEY) || null;
            const fieldName = resolveTaskChecklistFieldName(typeDef);
            const rows = await listChecklistsForNodeField({
              baseKey,
              fieldName,
              nodeKey: stepNodeKey,
            });
            const canonical = pickCanonicalChecklist(rows);
            const snapshotItems = Array.isArray(canonical?.sourceSnapshot?.items)
              ? canonical.sourceSnapshot.items
              : [];
            const progress = computeWorkflowTaskChecklistProgress(recipe.items, snapshotItems);
            if (isWorkflowTaskChecklistBlockingComplete(targetTask, progress)) {
              return;
            }
          } catch (error) {
            console.warn('[useMissionRunActions] Task Checklist gate failed:', error);
            return;
          }
        }
      }
    }

    const nextBlockingReason = normalizedStatus === 'blocked'
      ? blockingReasonOverride || String(targetTask?.blockingReason || '').trim()
      : '';
    const nextFailureDetail = normalizedStatus === 'failed'
      ? failureDetailOverride || String(targetTask?.failureDetail || '').trim()
      : '';

    if (normalizedStatus === 'blocked' && !nextBlockingReason) return;

    const nowMs = Date.now();
    const completionOutcomeKey = normalizedStatus === 'complete'
      ? (
        String(options?.completionOutcomeKey || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
        || WORKFLOW_OUTCOME_COMPLETE
      )
      : '';
    const runEdges = Array.isArray(missionRun?.edges) && missionRun.edges.length > 0
      ? missionRun.edges
      : template?.edges;
    const recomputedTasks = recomputeMissionRunTasks(missionRun.tasks.map((task) => {
      if (task.key !== normalizedTaskKey) return task;

      const nextTask = {
        ...task,
        blockingReason: normalizedStatus === 'blocked' ? nextBlockingReason : '',
        failureDetail: nextFailureDetail,
        status: normalizedStatus,
        updatedAtMs: nowMs,
      };

      if (completionOutcomeKey) {
        nextTask.completionOutcomeKey = completionOutcomeKey;
      } else {
        delete nextTask.completionOutcomeKey;
      }

      return nextTask;
    }), runEdges);
    const nextTargetTask = recomputedTasks.find((task) => task.key === normalizedTaskKey) || null;
    const nextTaskEventsWithStatus = appendStatusTransitionTaskEvents({
      createdAtMs: nowMs,
      nextTask: nextTargetTask,
      previousTask: targetTask,
      previousTaskEvents: missionRun.taskEvents,
    });
    const nextTaskEvents = appendDerivedMissionTaskEvents({
      createdAtMs: nowMs,
      nextTasks: recomputedTasks,
      previousTaskEvents: nextTaskEventsWithStatus,
      previousTasks: missionRun.tasks,
    });

    const nextRun = {
      ...missionRun,
      edges: Array.isArray(runEdges) ? runEdges : (missionRun.edges || []),
      taskEvents: nextTaskEvents,
      tasks: applyMissionTaskActivity(recomputedTasks, nextTaskEvents),
      updatedAtMs: nowMs,
    };

    const nextMissionRuns = buildNextMissionRunsPatch(node?.missionRuns, template.key, nextRun);

    setSavingMissionKey(template.key);
    try {
      await onNodeChange(node.key, {
        missionRuns: nextMissionRuns,
      });
      setBlockingTaskKey((prev) => prev === normalizedTaskKey ? '' : prev);
      setBlockingReasonErrors((prev) => {
        if (!prev[normalizedTaskKey]) return prev;
        const next = { ...prev };
        delete next[normalizedTaskKey];
        return next;
      });
    } finally {
      setSavingMissionKey('');
    }
  }, [node?.key, node?.missionRuns, onNodeChange]);

  const handleTaskStatusSelect = useCallback((template, missionRun, task, nextStatus) => {
    const normalizedTaskKey = String(task?.key || '').trim();
    const normalizedStatus = String(nextStatus || '').trim();
    if (!normalizedTaskKey || !normalizedStatus) return;

    if (normalizedStatus === 'blocked') {
      const existingReason = String(task?.blockingReason || '').trim();

      setBlockingTaskKey(normalizedTaskKey);
      setBlockingReasonDrafts((prev) => ({
        ...prev,
        [normalizedTaskKey]: prev[normalizedTaskKey] ?? existingReason,
      }));

      if (!existingReason) return;
    }

    handleTaskStatusChange(template, missionRun, normalizedTaskKey, normalizedStatus, {
      blockingReason: String(task?.blockingReason || '').trim(),
    });
  }, [handleTaskStatusChange]);

  const handleBlockingReasonDraftChange = useCallback((taskKey, nextValue) => {
    const normalizedTaskKey = String(taskKey || '').trim();
    if (!normalizedTaskKey) return;

    setBlockingReasonDrafts((prev) => ({
      ...prev,
      [normalizedTaskKey]: String(nextValue || ''),
    }));
    setBlockingReasonErrors((prev) => {
      if (!prev[normalizedTaskKey]) return prev;
      const next = { ...prev };
      delete next[normalizedTaskKey];
      return next;
    });
  }, []);

  const handleNudgeTask = useCallback(async (template, missionRun, task) => {
    if (!template?.key || !node?.key || !missionRun || typeof onNodeChange !== 'function') return;

    const normalizedTaskKey = String(task?.key || '').trim();
    if (!normalizedTaskKey || !canNudgeMissionTask(task, user)) return;

    const targetTask = missionRun.tasks.find((entry) => entry.key === normalizedTaskKey) || null;
    if (!targetTask) return;

    const actorKey = getMissionTaskPersonKey(user);
    const actorLabel = getMissionTaskPersonLabel(user) || 'Someone';
    const nudgeDetail = buildMissionTaskNudgeDetail(targetTask);
    const hasNotificationRecipients = (Array.isArray(targetTask.assignees) ? targetTask.assignees : [])
      .some((recipient) => {
        const recipientKey = getMissionTaskPersonKey(recipient);
        return recipientKey && recipientKey !== actorKey;
      });
    if (!hasNotificationRecipients) return;
    const nowMs = Date.now();
    const nextTaskEvents = [
      ...normalizeMissionTaskEvents(missionRun.taskEvents),
      buildMissionTaskEvent({
        actorName: actorLabel,
        actorUserKey: actorKey,
        createdAtMs: nowMs,
        detail: nudgeDetail,
        task: targetTask,
        type: NODE_MISSION_TASK_EVENT_TYPE.TASK_NUDGED,
      }),
    ];
    const nextRun = {
      ...missionRun,
      taskEvents: nextTaskEvents,
      tasks: applyMissionTaskActivity(missionRun.tasks, nextTaskEvents),
      updatedAtMs: nowMs,
    };
    const nextMissionRuns = buildNextMissionRunsPatch(node?.missionRuns, template.key, nextRun);

    setNudgingTaskKey(normalizedTaskKey);
    setSavingMissionKey(template.key);
    try {
      await onNodeChange(node.key, {
        missionRuns: nextMissionRuns,
      });
    } finally {
      setNudgingTaskKey('');
      setSavingMissionKey('');
    }
  }, [node?.key, node?.missionRuns, onNodeChange, user]);

  const handleUnblockTask = useCallback(async (template, missionRun, taskKey) => {
    const normalizedTaskKey = String(taskKey || '').trim();
    if (!normalizedTaskKey) return;

    const targetTask = missionRun?.tasks?.find((task) => task.key === normalizedTaskKey) || null;
    if (!canUnblockMissionTask(targetTask)) return;

    await handleTaskStatusChange(template, missionRun, normalizedTaskKey, 'inProgress');

    setBlockingReasonDrafts((prev) => {
      if (!prev[normalizedTaskKey]) return prev;
      const next = { ...prev };
      delete next[normalizedTaskKey];
      return next;
    });
  }, [handleTaskStatusChange]);

  const handleReopenTask = useCallback((template, missionRun, taskKey) => {
    if (!template?.key || !node?.key || !missionRun || typeof onNodeChange !== 'function') return;

    const normalizedTaskKey = String(taskKey || '').trim();
    if (!normalizedTaskKey) return;

    const targetTask = missionRun.tasks.find((task) => task.key === normalizedTaskKey) || null;
    if (!canReopenMissionTask(targetTask)) return;

    const cascadeTasks = collectMissionTaskReopenCascadeTasks(missionRun, normalizedTaskKey);
    setReopenConfirm({
      message: buildReopenMissionTaskConfirmMessage(targetTask, cascadeTasks),
      missionRun,
      taskKey: normalizedTaskKey,
      template,
      title: String(targetTask?.title || '').trim() || 'task',
    });
  }, [node?.key, onNodeChange]);

  const setReopenConfirmOpen = useCallback((open) => {
    if (!open) setReopenConfirm(null);
  }, []);

  const handleConfirmReopenTask = useCallback(async () => {
    const pending = reopenConfirm;
    setReopenConfirm(null);
    if (!pending?.template?.key || !pending?.missionRun || !pending?.taskKey) return;
    if (!node?.key || typeof onNodeChange !== 'function') return;

    const reopened = reopenMissionRunTask({
      missionRun: pending.missionRun,
      nowMs: Date.now(),
      taskKey: pending.taskKey,
    });
    if (!reopened) return;

    const nextRun = {
      ...pending.missionRun,
      taskEvents: reopened.taskEvents,
      tasks: reopened.tasks,
      updatedAtMs: Date.now(),
    };
    const nextMissionRuns = buildNextMissionRunsPatch(
      node?.missionRuns,
      pending.template.key,
      nextRun
    );

    setSavingMissionKey(pending.template.key);
    try {
      await onNodeChange(node.key, {
        missionRuns: nextMissionRuns,
      });
    } finally {
      setSavingMissionKey('');
    }
  }, [node?.key, node?.missionRuns, onNodeChange, reopenConfirm]);

  const handleSaveBlockingReason = useCallback(async (template, missionRun, taskKey) => {
    const normalizedTaskKey = String(taskKey || '').trim();
    if (!normalizedTaskKey) return;

    const cleanedReason = String(blockingReasonDrafts[normalizedTaskKey] || '').trim();
    if (!cleanedReason) {
      setBlockingReasonErrors((prev) => ({
        ...prev,
        [normalizedTaskKey]: 'Blocking reason is required.',
      }));
      return;
    }

    await handleTaskStatusChange(template, missionRun, normalizedTaskKey, 'blocked', {
      blockingReason: cleanedReason,
    });
  }, [blockingReasonDrafts, handleTaskStatusChange]);

  return {
    blockingReasonDrafts,
    blockingReasonErrors,
    blockingTaskKey,
    handleBlockingReasonDraftChange,
    handleCancelMission,
    handleConfirmReopenTask,
    handleLaunchMission,
    handleNudgeTask,
    handleReopenTask,
    handleSaveBlockingReason,
    handleTaskStatusChange,
    handleTaskStatusSelect,
    handleUnblockTask,
    nudgingTaskKey,
    reopenConfirm,
    savingMissionKey,
    setBlockingTaskKey,
    setReopenConfirmOpen,
  };
}
